import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EntityLink from "../components/EntityLink";
import Finance from "./Finance";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/domain";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowLeft, ChevronDown, Banknote, Users } from "lucide-react";
import { toast } from "sonner";
import AIPanel from "../components/AIPanel";
import FinanceIntegrityPanel from "../components/FinanceIntegrityPanel";
import useModules from "../hooks/useModules";
import DateInput from "../components/DateInput";
import PageHeader from "../components/PageHeader";
import HeroKPI from "../components/HeroKPI";
import EmptyState from "../components/EmptyState";
import useChartColors from "../hooks/useChartColors";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    LineChart, Line, Cell,
} from "recharts";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSDShort = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const lastMonthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10); };
const lastMonthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10); };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };
const ytdStart = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const MODULE_COLORS = { AP: "#1E2A5A", IC: "#C8102E", CN: "#F59E0B" };

export default function MasterFinance() {
    const navigate = useNavigate();
    const chartColors = useChartColors();
    const { modules } = useModules();
    const [from, setFrom] = useState(monthStart());
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);
    const [hero, setHero] = useState(null);
    const [cbm, setCbm] = useState([]);
    const [ppeSummary, setPpeSummary] = useState(null);
    const [cbpRollup, setCbpRollup] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/dashboard/master-overview?date_from=${from}&date_to=${to}`),
            api.get(`/dashboard/master-hero`).catch(() => ({ data: null })),
            api.get(`/dashboard/cost-by-month?months=6`).catch(() => ({ data: { months: [] } })),
            api.get(`/ppe/summary?date_from=${from}&date_to=${to}`).catch(() => ({ data: null })),
            api.get(`/payroll/cost-by-project-rollup?date_from=${from}&date_to=${to}`).catch(() => ({ data: null })),
        ])
            .then(([o, hp, cbmr, pe, cbp]) => {
                setData(o.data);
                setHero(hp?.data || null);
                setCbm(cbmr?.data?.months || []);
                setPpeSummary(pe?.data || null);
                setCbpRollup(cbp?.data || null);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [from, to]);

    const k = data?.kpis;
    const pipeline = data?.funding_pipeline;
    const kpis = hero?.kpis;

    const costByModuleChart = useMemo(() => (data?.per_module || []).map((m) => ({
        code: m.code, cost: m.net_payable_total || 0, color: MODULE_COLORS[m.code] || "#1E2A5A",
    })), [data]);

    const costByMonthChart = useMemo(() => cbm.map((r) => ({
        month: r.month.slice(5), // 'YYYY-MM' -> 'MM'
        cost: r.cost,
        periods: r.periods,
    })), [cbm]);

    const cashPaidSparkline = useMemo(() => cbm.map((r) => r.cost || 0), [cbm]);

    if (loading || !data) {
        return (
            <section className="space-y-6" data-testid="master-finance">
                <PageHeader title="Master Finance" subtitle="Loading consolidated view…" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="asl-card p-5 space-y-3">
                            <div className="asl-shimmer h-3 w-24" />
                            <div className="asl-shimmer h-8 w-32" />
                            <div className="asl-shimmer h-3 w-20" />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="space-y-6" data-testid="master-finance">
            <PageHeader
                title="Master Finance"
                subtitle="Consolidated view across AP · IC · Construction"
                actions={
                    <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="master-back">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Home
                    </Button>
                }
            />

            <div>
                <AIPanel allowModulePicker modules={modules} />
            </div>

            {/* Filter chips */}
            <div className="flex items-end gap-2 flex-wrap p-3 rounded-lg" style={{ background: "var(--asl-surface)", border: "1px solid var(--asl-divider)" }}>
                <Preset onClick={() => { setFrom(weekStart()); setTo(today()); }} label="This Week" testid="preset-week" />
                <Preset onClick={() => { setFrom(monthStart()); setTo(today()); }} label="This Month" testid="preset-month" />
                <Preset onClick={() => { setFrom(lastMonthStart()); setTo(lastMonthEnd()); }} label="Last Month" testid="preset-lastmonth" />
                <Preset onClick={() => { setFrom(ytdStart()); setTo(today()); }} label="YTD" testid="preset-ytd" />
                <div><div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--asl-text-3)" }}>From</div><DateInput value={from} className="w-40" onChange={setFrom} data-testid="mf-from" /></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--asl-text-3)" }}>To</div><DateInput value={to} className="w-40" onChange={setTo} data-testid="mf-to" /></div>
            </div>

            {/* Hero KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="master-hero-kpis">
                <HeroKPI
                    label="Total Active Employees"
                    value={kpis?.active_employees?.value ?? k.total_active_workers}
                    testid="master-hero-employees"
                />
                <HeroKPI
                    label="Weekly Cost"
                    value={fmtUSD(kpis?.week_cost?.value || k.total_gross)}
                    delta={kpis?.week_cost?.prev != null ? {
                        value: kpis.week_cost.delta_direction === "flat" ? "0" :
                            `${kpis.week_cost.delta_direction === "up" ? "+" : "−"}${fmtUSDShort(Math.abs(kpis.week_cost.delta_absolute))}`,
                        direction: kpis.week_cost.delta_direction,
                        label: kpis.week_cost.delta_label,
                    } : null}
                    tone="accent"
                    testid="master-hero-week-cost"
                />
                <HeroKPI
                    label="Cash Paid YTD"
                    value={fmtUSD(kpis?.cash_paid_ytd?.value || k.cash_paid)}
                    sparkline={cashPaidSparkline}
                    sub="last 6 months"
                    testid="master-hero-cash-paid"
                />
                <HeroKPI
                    label="Open Exceptions"
                    value={kpis?.exceptions_open?.value ?? 0}
                    tone={(kpis?.exceptions_open?.value ?? 0) > 0 ? "danger" : "default"}
                    testid="master-hero-exceptions"
                />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartCard title="Cost by Module · this range" testid="master-chart-cbm">
                    {costByModuleChart.length === 0 || costByModuleChart.every((m) => !m.cost) ? (
                        <EmptyState icon={Banknote} title="No cost in this range" description="Prepare payroll periods to see the module-level breakdown." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220} minWidth={200} minHeight={200}>
                            <BarChart data={costByModuleChart} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                                <CartesianGrid horizontal={false} stroke="var(--asl-divider)" strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(v) => fmtUSDShort(v)} tickLine={false} axisLine={false} />
                                <YAxis type="category" dataKey="code" width={40} tickLine={false} axisLine={false} />
                                <Tooltip formatter={(v) => fmtUSD(v)} />
                                <Bar dataKey="cost" radius={[0, 6, 6, 0]}>
                                    {costByModuleChart.map((m, i) => <Cell key={i} fill={m.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="Cost by Month · last 6 months" testid="master-chart-cbmonth">
                    {costByMonthChart.length === 0 || costByMonthChart.every((m) => !m.cost) ? (
                        <EmptyState icon={Users} title="No monthly cost yet" description="Once payroll periods land, monthly aggregates appear here." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220} minWidth={200} minHeight={200}>
                            <LineChart data={costByMonthChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="var(--asl-divider)" strokeDasharray="3 3" />
                                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                                <YAxis tickFormatter={(v) => fmtUSDShort(v)} tickLine={false} axisLine={false} width={54} />
                                <Tooltip formatter={(v) => fmtUSD(v)} />
                                <Line type="monotone" dataKey="cost" stroke={chartColors.series[0]} strokeWidth={2} dot={{ r: 4, fill: chartColors.series[3] }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            {/* Per-module summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="mf-modules">
                {data.per_module.map((m) => (
                    <div key={m.module_id} className="asl-card p-5 space-y-3" data-testid={`mf-mod-${m.code}`}>
                        <div className="flex items-baseline justify-between">
                            <EntityLink to={`/m/${m.code.toLowerCase()}`} testid={`mf-mod-title-${m.code}`}>
                                <h3 className="text-card-title inline">{m.name}</h3>
                            </EntityLink>
                            <span className="text-label" style={{ color: "var(--asl-crimson-2)" }}>{m.code}</span>
                        </div>
                        <div className="text-xs" style={{ color: "var(--asl-text-2)" }}>{m.workers_active} workers · {m.period_count} periods</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <Fact label="Gross" value={fmtUSD(m.gross_total)} />
                            <Fact label="Net" value={fmtUSD(m.net_payable_total)} accent />
                            <Fact label="Mgmt fee" value={fmtUSD(m.management_fee)} />
                            <Fact label="Outstanding" value={fmtUSD(m.outstanding)} />
                        </div>
                        <button onClick={() => navigate(`/m/${m.code.toLowerCase()}/finance`)}
                            className="text-xs font-semibold flex items-center gap-1 hover:underline"
                            style={{ color: "var(--asl-navy-2)" }}
                            data-testid={`mf-open-${m.code}`}>
                            Open Module Finance <ArrowRight className="h-3 w-3" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Payroll — Cost by Project cross-module rollup (preserved) */}
            {cbpRollup && cbpRollup.by_project?.length > 0 && (
                <div className="asl-card p-5" data-testid="master-cbp-section">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="asl-section-title">Payroll — Cost by Project</div>
                            <div className="text-xs mt-1" style={{ color: "var(--asl-text-3)" }}>
                                Aggregated over {cbpRollup.period_count} payroll period(s), {cbpRollup.modules?.length || 0} module(s), for {formatDate(cbpRollup.date_from)} → {formatDate(cbpRollup.date_to)}.
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-label">Grand total</div>
                            <div className="text-metric-sm mono" style={{ color: "var(--asl-crimson-2)" }} data-testid="master-cbp-total">{fmtUSD(cbpRollup.total_cost)}</div>
                        </div>
                    </div>
                    <table className="asl-table asl-table--zebra" data-testid="master-cbp-table">
                        <thead>
                            <tr>
                                <th>Project</th>
                                <th>Modules</th>
                                <th className="text-right">Worker Days</th>
                                <th className="text-right">Cost</th>
                                <th className="text-right">% of Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cbpRollup.by_project.map((r, i) => {
                                const pct = cbpRollup.total_cost ? (r.cost / cbpRollup.total_cost) * 100 : 0;
                                return (
                                    <tr key={r.project_id || `none-${i}`} data-testid={`master-cbp-row-${r.project_id || 'none'}`}>
                                        <td className="font-medium" style={{ color: "var(--asl-text)" }}>{r.project_name}</td>
                                        <td>
                                            <div className="flex flex-wrap gap-1">
                                                {(r.by_module || []).map((m) => (
                                                    <EntityLink key={m.module_id} variant="chip"
                                                        to={`/m/${(m.module_code || "").toLowerCase()}`}
                                                        testid={`master-cbp-mod-${m.module_code || m.module_id}`}
                                                        title={`${m.module_name}: ${fmtUSD(m.cost)}`}>
                                                        {m.module_code} · {fmtUSD(m.cost)}
                                                    </EntityLink>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="mono text-right">{r.worker_days}</td>
                                        <td className="mono text-right font-semibold" style={{ color: "var(--asl-navy-2)" }}>{fmtUSD(r.cost)}</td>
                                        <td className="mono text-right">{pct.toFixed(1)}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Clothing & PPE — cross-module rollup (preserved) */}
            {ppeSummary && (
                <div className="asl-card p-5" data-testid="master-ppe-section">
                    <div className="asl-section-title mb-3">Clothing & PPE — cross-module</div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="master-ppe-kpis">
                        <MiniStat label="Total spend" value={fmtUSD(ppeSummary.totals?.total_spend)} accent />
                        <MiniStat label="Items" value={ppeSummary.totals?.total_items || 0} />
                        <MiniStat label="In circulation" value={ppeSummary.totals?.in_circulation || 0} />
                        <MiniStat label="Returned" value={ppeSummary.totals?.returned || 0} />
                        <MiniStat label="Replaced" value={ppeSummary.totals?.replaced || 0} />
                        <MiniStat label="Lost/Damaged" value={(ppeSummary.totals?.lost || 0) + (ppeSummary.totals?.damaged || 0)} />
                    </div>
                    {(ppeSummary.by_module || []).length > 0 && (
                        <table className="asl-table asl-table--zebra" data-testid="master-ppe-by-module">
                            <thead>
                                <tr>
                                    <th>Module</th>
                                    <th className="text-right">Count</th>
                                    <th className="text-right">Spend</th>
                                    <th className="text-right"> </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ppeSummary.by_module.map((r) => (
                                    <tr key={r.module_id} data-testid={`master-ppe-row-${r.module_code || r.module_id}`}>
                                        <td className="font-medium" style={{ color: "var(--asl-text)" }}>{r.module_name || r.module_id} <span className="text-label ml-1" style={{ color: "var(--asl-crimson-2)" }}>{r.module_code}</span></td>
                                        <td className="mono text-right">{r.count}</td>
                                        <td className="mono text-right font-semibold" style={{ color: "var(--asl-navy-2)" }}>{fmtUSD(r.spend)}</td>
                                        <td className="text-right">
                                            <button onClick={() => r.module_code && navigate(`/m/${r.module_code.toLowerCase()}/ppe`)}
                                                className="text-xs font-semibold hover:underline" style={{ color: "var(--asl-navy-2)" }}>
                                                Open →
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Finance ledger (preserved) */}
            <div className="asl-card p-1">
                <Finance />
            </div>

            {/* Finance integrity check (preserved) */}
            <FinanceIntegrityPanel from={from} to={to} />

            {/* "More metrics" — original 8-KPI strip + funding pipeline collapsed */}
            <details className="asl-card p-0 overflow-hidden group" data-testid="master-more-metrics">
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-[color:var(--asl-bg-alt)]">
                    <div className="asl-section-title">More metrics</div>
                    <ChevronDown className="h-4 w-4 opacity-60 transition-transform group-open:rotate-180" />
                </summary>
                <div className="p-5 pt-0 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="master-more-kpis">
                        <MiniStat label="Workers" value={k.total_active_workers} />
                        <MiniStat label="Gross" value={fmtUSD(k.total_gross)} />
                        <MiniStat label="Net" value={fmtUSD(k.total_net_payable)} accent />
                        <MiniStat label="Mgmt fee" value={fmtUSD(k.total_management_fee)} />
                        <MiniStat label="Funding req" value={fmtUSD(k.funding_required)} />
                        <MiniStat label="Outstanding" value={fmtUSD(k.funding_outstanding)} accent={k.funding_outstanding > 0} />
                        <MiniStat label="Cash released" value={fmtUSD(k.cash_released)} />
                        <MiniStat label="Cash paid" value={fmtUSD(k.cash_paid)} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="master-more-pipeline">
                        {Object.entries(pipeline || {}).map(([key, v]) => (
                            <MiniStat key={key} label={key.replace(/_/g, " ")} value={fmtUSD(v)} testid={`master-more-pipeline-${key}`} />
                        ))}
                    </div>
                </div>
            </details>
        </section>
    );
}

function ChartCard({ title, children, testid }) {
    return (
        <div className="asl-card p-5" data-testid={testid}>
            <div className="asl-section-title mb-3">{title}</div>
            {children}
        </div>
    );
}
function Fact({ label, value, accent }) {
    return (
        <div>
            <div className="text-label">{label}</div>
            <div className="text-sm mono font-semibold" style={{ color: accent ? "var(--asl-crimson-2)" : "var(--asl-navy-2)" }}>{value}</div>
        </div>
    );
}
function MiniStat({ label, value, accent, testid }) {
    return (
        <div className="asl-card p-3" data-testid={testid || `master-mini-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-label">{label}</div>
            <div className="text-base font-bold mono mt-1" style={{ color: accent ? "var(--asl-crimson-2)" : "var(--asl-text)" }}>{value}</div>
        </div>
    );
}
function Preset({ onClick, label, testid }) {
    return <Button variant="outline" size="sm" onClick={onClick} data-testid={testid}>{label}</Button>;
}
